/* Verbatim port of renderAppendix + renderAssetRows from
 * service/review/index.html (~3165-3260) — Appendix: full inventory,
 * vocabulary, coverage accounting, research dossier, internal QA.
 *
 * evidenceTier (~3154-3163) was already ported into ui/drawer.ts by an
 * earlier task, whose own doc comment calls out that this renderAssetRows
 * port should import it rather than re-port — done below. (humanField,
 * ported alongside it in the same file, is NOT used by either function
 * here — only by ui/drawer.ts's own openDrawer — so it isn't imported.)
 *
 * PARAMETERIZED (rule a): renderAppendix/renderAssetRows read the global
 * STATE (STATE.coverage, STATE.assets) — take `state: AppPayloadState` as
 * an explicit first parameter. FLAGGED (ported by an earlier task into
 * state/ui.ts's `flaggedStore`) is read via `flaggedStore.get()`.
 *
 * DEVIATION (closures, not new module state): the inventory filter inputs
 * (#fSearch/#fCategory/#fType/#fStatus) still hold their own state as DOM
 * value/selection — no separate filter-state object, same as the
 * original.
 *
 * F3: innerHTML → lit render(). The filter <select>/<input> population
 * (originally `sel.insertAdjacentHTML('beforeend', ...)` in a post-render
 * forEach, with `sel.onchange`/`input.oninput` wired imperatively
 * afterward) now builds the `<option>` lists as plain arrays BEFORE the
 * render() call and wires `@change`/`@input` directly on the elements in
 * the template — cleaner than keeping the DOM-insertion loop, and every
 * filter element is INSIDE this section's own container (convention #9:
 * no reason to keep it imperative). The two `tr.click` tables (review,
 * durability-disagreement) get `@click` directly instead of the trailing
 * querySelectorAll wiring; #assetRows' own rows are wired inside
 * renderAssetRows (unchanged split — see below).
 */
import { html, nothing, render } from 'lit';
import { GLOSSARY } from '../lib/glossary.ts';
import { faviconUrl } from '../lib/format.ts';
import { chipT, tipT } from '../lib/templates.ts';
import { BAD_STATUS, CAT_LABEL } from '../derive/axes.ts';
import { evidenceTier, openDrawer } from '../ui/drawer.ts';
import { flaggedStore } from '../state/ui.ts';
import { renderDossier } from './dossier.ts';
import {
  type PlaneProvenance,
  planePillClass,
  planeProvenanceLine,
  planeState,
} from '../derive/planes.ts';
import type { AppPayloadState } from '../derive/state-shape.ts';

export function renderAppendix(state: AppPayloadState): void {
  const el = document.getElementById('appendixBody')!;
  const cov = Object.entries(state.coverage || {});
  // The §0 coverage panel renders iff the run has phase* coverage keys; only
  // then may this appendix intro point at it (legacy runs have neither).
  const hasPanel = cov.some(([k]) => /^phase\d+/.test(k));
  const disagree = state.assets.filter((a) => a.fields && a.fields.impactDurability && a.durability && a.fields.impactDurability !== a.durability);
  const review = state.assets.filter((a) => a.needsReview);

  const categories = [...new Set(state.assets.map((a) => a.category).filter(Boolean))].sort();
  const types = [...new Set(state.assets.map((a) => a.assetType).filter(Boolean))].sort();
  const statuses = [...new Set(state.assets.map((a) => a.status).filter(Boolean))].sort();

  // Coverage-accounting honesty (H1): the old copy asserted the deferred
  // Search/AI phases "were completed by the enrichment stage" — FALSE the
  // moment a plane is skipped by `include` or dark for want of a credential.
  // Render each cross-reference plane's stamped provenance instead: neutral for
  // a deliberately `skipped` plane, amber-with-reason for `unavailable`/`error`
  // (never red — our debt, not client adversity). A legacy run (no
  // planeStatus) gets a promise-free neutral intro.
  const covPlanes: PlaneProvenance[] = (['impact', 'aiq'] as const)
    .map((p) => planeProvenanceLine(p, planeState(state.planeStatus, p)))
    .filter((x): x is PlaneProvenance => x != null);
  // Merged H1 (planeStatus provenance) + C1 (reconciled-panel framing): the
  // intro points at the §0 Coverage confidence panel as the reconciled source
  // (NO "may lag" self-disclaimer — C1 removed it; the panel reconciles the
  // deferred Search/AI layers), AND renders each cross-reference plane's honest
  // status pill (H1: neutral for a skipped plane, amber-with-reason for
  // unavailable/error). Gated on cov.length + hasPanel like the rest of C1 so a
  // legacy run never references a panel that doesn't exist.
  const covIntro = !cov.length
    ? nothing
    : html`<p style="font-size:12.5px;color:var(--ink-2);margin-bottom:8px;line-height:1.55">${hasPanel
        ? html`These are the mapping run's own per-phase notes — the detail behind the reconciled <b>Coverage confidence</b> panel at the top of the executive summary, which reconciles the deferred Search (§1) / AI-narrative (§3) layers to the measured counts used across the page. This block adds the full per-phase prose, including every thin-spot caveat.`
        : html`These are the mapping run's own per-phase notes — phase-by-phase search/fetch accounting, with every thin-spot caveat flagged.`}</p>${state.planeStatus && covPlanes.length
        ? html`<div style="display:flex;flex-wrap:wrap;gap:6px;margin-bottom:12px">${covPlanes.map((pv) => html`<span class="${planePillClass(pv.tone)}">${pv.text}</span>`)}</div>`
        : nothing}`;

  render(html`
    <details class="appx">
      <summary>Full asset inventory<span class="sub">every mapped surface, filterable</span><span class="arrow">▶</span></summary>
      <div class="abody">
        <div class="filters">
          <input type="search" id="fSearch" placeholder="Search assets…" @input=${() => renderAssetRows(state)}>
          <select id="fCategory" @change=${() => renderAssetRows(state)}><option value="">All categories</option>${categories.map((v) => html`<option>${v}</option>`)}</select>
          <select id="fType" @change=${() => renderAssetRows(state)}><option value="">All types</option>${types.map((v) => html`<option>${v}</option>`)}</select>
          <select id="fStatus" @change=${() => renderAssetRows(state)}><option value="">All statuses</option>${statuses.map((v) => html`<option>${v}</option>`)}</select>
          <span class="count" id="fCount"></span>
        </div>
        <div style="overflow-x:auto"><table class="tbl"><thead><tr><th>Asset</th><th>Category</th><th>Type</th><th>Status</th><th>Scope</th><th>Evidence</th><th>⚑</th></tr></thead>
        <tbody id="assetRows"></tbody></table></div>
      </div>
    </details>

    <details class="appx">
      <summary>Vocabulary<span class="sub">every term this report uses, in plain English</span><span class="arrow">▶</span></summary>
      <div class="abody"><dl class="covlist" style="grid-template-columns:190px 1fr">${Object.entries(GLOSSARY).map(([k,v]) => html`<dt>${k}</dt><dd>${v}</dd>`)}</dl></div>
    </details>

    <details class="appx">
      <summary>Research coverage<span class="sub">phase-by-phase accounting, thin spots flagged</span><span class="arrow">▶</span></summary>
      <div class="abody">
      ${covIntro}
      <dl class="covlist">${cov.length ? cov.map(([k,v]) => {
        const thin = /THIN:/.test(v);
        const parts = String(v).split(/THIN:/);
        return html`<dt>${k.replace(/_/g,' ')}</dt><dd>${parts[0]}${thin ? html`<div class="thin" style="margin-top:4px">THIN: ${parts[1]}</div>` : nothing}</dd>`;
      }) : html`<dt>coverage</dt><dd>No coverage summary in this run.</dd>`}</dl></div>
    </details>

    <details class="appx">
      <summary>Research dossier<span class="sub">the agent's narrative report</span><span class="arrow">▶</span></summary>
      <div class="abody dossier" id="dossierBody"></div>
    </details>

    <details class="appx">
      <summary>Internal QA<span class="sub">review flags, evidence tiers, measurement disagreements — internal only</span><span class="arrow">▶</span></summary>
      <div class="abody">
        ${review.length ? html`<h3 style="font-size:14px;margin:8px 0">Flagged for human review before client use (${review.length})</h3>
        <table class="tbl"><tbody>${review.map((a) => html`<tr class="click" data-id="${a.id}" @click=${() => openDrawer(state, a.id)}><td style="width:34%"><b>${a.title}</b></td><td style="font-size:12.5px;color:var(--ink-2)">${a.reviewReason||''}</td></tr>`)}</tbody></table>` : html`<p style="font-size:13px">No assets flagged for review.</p>`}
        ${disagree.length ? html`<h3 style="font-size:14px;margin:18px 0 8px">Durability: research judgment vs IMPACT measurement (${disagree.length} disagree)</h3>
        <p style="font-size:12.5px;color:var(--ink-2);margin-bottom:8px">Where the research agent judged a result ${tipT('sticky','sticky')}/${tipT('volatile','volatile')} differently from IMPACT's measured 6-month rank variance. The measured value wins; the judgment is listed for calibration.</p>
        <table class="tbl"><tbody>${disagree.map((a) => html`<tr class="click" data-id="${a.id}" @click=${() => openDrawer(state, a.id)}><td><b>${a.title}</b></td><td class="num">judged ${a.durability}</td><td class="num">measured ${a.fields?.impactDurability}</td></tr>`)}</tbody></table>` : nothing}
        ${flaggedStore.get().length ? html`<h3 style="font-size:14px;margin:18px 0 8px">Flagged from the IMPACT view (${flaggedStore.get().length})</h3>
        <p style="font-size:12.5px;color:var(--ink-2);margin-bottom:8px">Results an AM flagged for mapping — feed these to the next research run.</p>
        <table class="tbl"><tbody>${flaggedStore.get().map((f) => html`<tr><td style="width:30%"><b>${f.title||''}</b></td><td style="font-family:var(--mono);font-size:11px;word-break:break-all">${f.url}</td><td class="num">${f.at||''}</td></tr>`)}</tbody></table>` : nothing}
        <h3 style="font-size:14px;margin:18px 0 8px">Evidence tiers</h3>
        <p style="font-size:12.5px;color:var(--ink-2);line-height:1.6">Every asset carries an evidence tier, visible in its detail drawer: <b>measured</b> (live IMPACT/AIQ data) › <b>corroborated</b> (cross-referenced by site markup or Wikidata) › <b>page-fetched</b> (content seen during research) › <b>AI-assessed</b> (agent inference — confirm before client use). Sentiment calls are always AI-assessed unless IMPACT-rated.</p>
      </div>
    </details>`, el);

  renderAssetRows(state);
  renderDossier(state);
}
export function renderAssetRows(state: AppPayloadState): void {
  const q = ((document.getElementById('fSearch') as HTMLInputElement).value||'').toLowerCase();
  const fc = (document.getElementById('fCategory') as HTMLSelectElement).value, ft = (document.getElementById('fType') as HTMLSelectElement).value, fs = (document.getElementById('fStatus') as HTMLSelectElement).value;
  const rows = state.assets.filter((a) => {
    if (q && !((a.title||'')+' '+(a.url||'')+' '+(a.notes||'')).toLowerCase().includes(q)) return false;
    if (fc && a.category !== fc) return false;
    if (ft && a.assetType !== ft) return false;
    if (fs && a.status !== fs) return false;
    return true;
  });
  document.getElementById('fCount')!.textContent = rows.length + ' / ' + state.assets.length;
  render(html`${rows.map((a) => {
    const ev = evidenceTier(a);
    return html`<tr class="click" data-id="${a.id}" @click=${() => openDrawer(state, a.id)}>
      <td>${a.url ? html`<img class="favi" src="${faviconUrl(a.url,32)}" alt="">` : nothing}${a.isCoreEntity ? '◆ ' : ''}${a.title || a.id}</td>
      <td>${chipT(CAT_LABEL[a.category] || a.category, a.category)}</td>
      <td style="font-size:12px">${a.assetType || ''}${a.platform ? ' · ' + a.platform : ''}</td>
      <td>${chipT(a.status, BAD_STATUS.includes(a.status) ? 'warn' : a.status === 'active' ? 'ok' : '')}</td>
      <td style="font-size:12px">${a.scope || ''}${a.region && a.region !== 'global' ? ' · ' + a.region : ''}</td>
      <td><span class="chip" title="${ev.why}">${ev.label}</span></td>
      <td>${a.needsReview ? html`<span class="chip warn">⚑</span>` : nothing}</td>
    </tr>`;
  })}`, document.getElementById('assetRows')!);
}
